<template>
  <div class="item-slider" :class="rootClasses">
    <client-only>
      <div class="item-slider__header" v-if="seeAllUrl || carouselTitle">
        <component
          :is="headlineTag"
          v-if="carouselTitle"
          class="item-slider__carousel-title"
          :class="headerClasses" v-html="carouselTitle" />
        <nuxt-link v-if="seeAllUrl" class="item-slider__see-all" :to="seeAllUrl">
          See all
        </nuxt-link>
      </div>
      <div class="item-slider__slider-container">
        <button class="item-slider__slider-navigation item-slider__slider-navigation--prev" @click="goPrev">
          <icon-angle-right class="item-slider__navigation-icon" />
        </button>
        <swiper-container
          v-if="itemType === 'content' || slides?.length > 0"
          :init="false"
          :key="uniqueId"
          :id="uniqueId"
          :ref="(el) => { initializeSlider(el) }"
          class="item-slider__container"
          :class="swiperContainerClass"
          :navigation="false"
          :slides-per-view="itemType === 'product' ? 2 : 1"
          :space-between="15"
          :loop="true"
          :autoplay="autoSlide"
          :speed="300"
          :lazy-load="lazyLoad">
          <slot v-if="itemType === 'content'" />
          <swiper-slide
            v-else
            v-for="slide in slides"
            :key="slide.title">
            <blog-tile
              v-if="itemType === 'article'"
              tile-style="slide"
              :post-image="slide.featuredImage?.node"
              :post-title="slide.title"
              :post-date="slide.dateGmt"
              :post-uri="slide.uri"
              :post-categories="slide.categories.nodes"
              :post-author="slide.author?.node"
              :enable-lazy-load="lazyLoad" />
            <product-tile
              v-else-if="itemType === 'product'"
              :product-id="slide.databaseId"
              :product-image="slideMediaItemUrl(slide)"
              :default-currency="slide.defaultCurrency"
              :product-link="slide.uri"
              :product-name="slide.name"
              :vendor-name="vendorData(slide).name"
              :vendor-slug="vendorData(slide).slug"
              :soundcloud-id="slide.productSpecs?.productSoundcloudCode"
              :soundcloud-id-type="slide.productSpecs?.soundcloudShortcode?.includes('/playlists/') ? 'playlist' : 'track'"
              :product-specs="slide.productSpecs"
              :product-variations="slide.variations?.nodes"
              :product-subscription-options="slide.productSubscriptionOptions"
              :enable-lazy-load="lazyLoad"
              :sale-badges="slide.productSaleBadges" />
          </swiper-slide>
        </swiper-container>
        <button class="item-slider__slider-navigation item-slider__slider-navigation--next" @click="goNext">
          <icon-angle-right class="item-slider__navigation-icon" />
        </button>
      </div>
    </client-only>
  </div>
</template>

<script lang="ts">
import { register } from 'swiper/element/bundle';
import { fetchSliderBlogs } from "~/hooks/queries/useBlogsDataQuery";
import type { ApiResult } from "~/stores/types/searchTermInterface";
import { defineAsyncComponent, nextTick, useRuntimeConfig } from '#imports';

const IconAngleRight = defineAsyncComponent(() => import('~/assets/icons/IconAngleRight.svg'));

export default {
  setup () {
    return {
      config: useRuntimeConfig(),
    };
  },
  components: {
    IconAngleRight,
  },
  async mounted() {
    register();

    let data;
    let slides = [];

    if (this.injectedSlides) {
      slides = this.injectedSlides;
    } else if (this.itemType === 'article') {
      data = await fetchSliderBlogs();

      slides = data?.posts?.nodes;
    } else if (this.itemType === 'product') {
      slides = await this.fetchProducts();
    }

    this.slides = slides;
  },
  data () {
    return {
      slides: [] as ApiResult[]|any[],
      swiperEl: null,
    };
  },
  props: {
    itemType: String as 'product'|'article'|'content'|string,
    carouselTitle: String,
    carouselTitleAlign: {
      type: String,
      default: 'left',
    },
    carouselTitleSize: {
      type: String,
      default: 'medium',
    },
    productCategories: String,
    contentUrl: String,
    seeAllUrl: String,
    itemPosition: String,
    injectedSlides: Array,
    autoSlide: Boolean,
    lazyLoad: Boolean,
  },
  computed: {
    sliderBreakPoints () {
      let slidesCount = [2,3,4,5,5,6,7];
      const breakpoints = [500,780,1024,1280,1550,1800,2560];
      const spaceBetween = [15,20,25,25,25,25,25];
      if (this.itemType === 'article') {
        slidesCount = [1,2,2,2,3,4,4];
      } else if (this.itemType === 'content') {
        slidesCount = [1,2,3,4,4,4,5];
      }
      const response = {};
      breakpoints.forEach((breakpoint, index) => {
        if (slidesCount[index]) {
          response[breakpoint] = {
            slidesPerView: slidesCount[index],
            spaceBetween: spaceBetween[index],
          };
        }
      });
      return response;
    },
    uniqueId () {
      const uniqueId = Math.random()
      return `${this.itemType}-${uniqueId}`;
    },
    headlineTag () {
      if (this.carouselTitleSize === 'small') {
        return 'h4';
      }

      if (this.carouselTitleSize === 'paragraph') {
        return 'p';
      }

      return this.carouselTitleSize === 'large'
        ? 'h2'
        : 'h3';
    },
    rootClasses () {
      return {
        'item-slider--content': this.itemType === 'content',
        'item-slider--image-tiles': this.itemType !== 'content',
      };
    },
    headerClasses () {
      return {
        'item-slider__carousel-title--left': this.carouselTitleAlign === 'left',
        'item-slider__carousel-title--center': this.carouselTitleAlign === 'center',
        'item-slider__carousel-title--right': this.carouselTitleAlign === 'right',
        'item-slider__carousel-title--small': this.carouselTitleSize === 'small',
        'item-slider__carousel-title--medium': this.carouselTitleSize === 'medium',
        'item-slider__carousel-title--large': this.carouselTitleSize === 'large',
      };
    },
    swiperContainerClass () {
      return {
        'item-slider__container--fixed-height': this.itemType === 'content',
      };
    },
  },
  methods: {
    vendorData (slide) {
      return slide.vendor
        ? slide.vendor
        : slide.productVendor;
    },
    async initializeSlider(el) {
      await nextTick();

      this.swiperEl = el;

      if (!this.swiperEl) {
        return;
      }

      const swiperParams = {
        breakpoints: this.sliderBreakPoints,
        autoplay: this.autoSlide ? {
          disableOnInteraction: true,
          pauseOnMouseEnter: true,
        } : false,
      };

      Object.assign(this.swiperEl, swiperParams);

      this.swiperEl.initialize();
    },
    slideMediaItemUrl(slide: { image: { mediaItemUrl: any; }; }) {
      if (! slide?.image?.mediaItemUrl) {
        return '' //@todo fallback product image
      }

      return slide.image.mediaItemUrl
    },
    async fetchProducts() {
      const productLimit = 10;
      let searchResults = [];
      const queryParams = [
        'type=product',
        'orderby=date',
        'order=desc'
      ];
      const baseUrl = `${this.config.public.baseUrl}/wp-json/content-search/v1/search`

      if (this.productCategories) {
        queryParams.push(`categories=${this.productCategories}`);
      }

      queryParams.push(`limit=${productLimit}`);

      const queryUrl = `${baseUrl}?${queryParams.join('&')}`;

      let data;

      try {
        const results = await fetch(queryUrl);
        data = await results.json();
      } catch (e) {
        return
      }

      if (data) {
        searchResults = data.results;
      }

      return searchResults;
    },
    goPrev () {
      if (this.swiperEl) {
        this.swiperEl.swiper.slidePrev();
      }
    },
    goNext () {
      if (this.swiperEl) {
        this.swiperEl.swiper.slideNext();
      }
    },
  },
  watch: {
    injectedSlides(newSlides) {
      this.slides = newSlides;
    },
  }
};
</script>

<style lang="scss">
@use "~/common/scss/helpers";
@use "~/common/scss/variables";
@use "~/common/scss/mq";

.item-slider {
  $root: &;

  height: 100%;
  margin-bottom: var(--item-slider-spacing, 2rem) !important; // Need to overwrite global styles
  width: 100%;

  .page & {
    margin-bottom: 4rem;
  }

  &__header {
    display: flex;
    justify-content: space-between;
  }

  &__carousel-title {
    flex-grow: 1;
    font-weight: 600;
    padding-bottom: 1rem;

    &--left {
      text-align: left;
    }

    &--center {
      text-align: center;
    }

    &--right {
      text-align: right;
    }

    &--small {
      font-size: 1rem;
    }

    &--medium {
      font-size: 1.4rem;
    }

    &--large {
      font-size: 2rem;
    }
  }

  &__see-all {
    font-weight: 600;
    text-decoration: none;
  }

  &__slider-container {
    margin: 0 0;
    position: relative;
  }

  &__container {
    --swiper-navigation-color: #{variables.$colorBlack};

    height: 100%;

    &--fixed-height {
      display: grid;
    }
  }

  &__slider-navigation {
    --arrow-offset: 0;

    font-size: 2rem;
    position: absolute;
    top: helpers.px-to-rem(150);
    transform: translateY(-50%);
    z-index: 20;

    @include mq.mobileOnly() {
      #{$root}--image-tiles & {
        color: #DDD;
      }
    }

    @include mq.aboveMobile() {
      --arrow-offset: -1rem;

      color: #000;
    }

    @include mq.aboveTablet() {
      --arrow-offset: -3rem;
    }

    &--prev {
      left: var(--arrow-offset);

      #{$root}__navigation-icon {
        transform: rotate(180deg);
      }
    }

    &--next {
      right: var(--arrow-offset);
    }
  }
}
</style>
